import { ChangeEvent, useState, useEffect } from 'react';
import { useSetRecoilState, useRecoilValue } from 'recoil';
import { useMutation } from '@apollo/client';
import { userInfoState } from 'states/CheckOutStates/atoms';
import { DASHBOARD_SEND_EMAIL_VERIFICATION, DASHBOARD_CHANGE_EMAIL } from 'graphql/service';
import { useAuth } from 'customHooks/useAuth';
import { useGlobalAlerts } from 'customHooks/useGlobalAlerts';

export const useChangeEmail = () => {
  const debug = false;

  const { showGlobalError, showGlobalMessage } = useGlobalAlerts();

  const { refreshUserInfo } = useAuth();

  useEffect(() => {
    refreshUserInfo();
  }, [refreshUserInfo]);

  const setUserInfo = useSetRecoilState(userInfoState);
  const userInfo = useRecoilValue(userInfoState);

  useEffect(() => {
    setEmailValue(userInfo.emailInput);
    setNewEmailValue(userInfo.emailInput);

  }, [userInfo]);

  const [emailValue, setEmailValue] = useState(userInfo.emailInput);
  const [newEmailValue, setNewEmailValue] = useState(userInfo.emailInput);

  const [changeEmailMoad, setChangeEmailMoadl] = useState(false);
  const [changeConfirmationMoadal, setChangeConfirmationMoadal] = useState(false);

  const [newEmailError, setNewEmailError] = useState(false);
  const [newEmailErrorText, setNewEmailErrorText] = useState('');

  const [newVerificationCodeError, setNewVerificationCodeError] = useState(false);
  const [newVerificationCodeErrorText, setNewVerificationCodeErrorText] = useState('');

  const [codeResent, setCodeResent] = useState(false);

  const CODE_LENGTH = 6;

  const [verificationCode, setVerificationCode] = useState<string[]>(Array(CODE_LENGTH).fill(''));

  function isValidEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  const [dashboardChangeEmail] = useMutation(DASHBOARD_CHANGE_EMAIL, {
    onCompleted: () => {
      setChangeConfirmationMoadal(false);
      setChangeEmailMoadl(false);
      setVerificationCode([]);

      setUserInfo((userInfo) => ({
        ...userInfo,
        emailInput: newEmailValue,
      }));

      refreshUserInfo();

      showGlobalMessage("Your e-mail has been successfully changed.");
    },
    onError: (error) => {
      const content = error.message;

      setChangeConfirmationMoadal(false);
      setVerificationCode([]);
      setNewEmailValue(emailValue);
      showGlobalError(content);

      if (debug) {
        console.log(error);
      }
    },
  });

  const [dashboardSendEmailVerification] = useMutation(DASHBOARD_SEND_EMAIL_VERIFICATION, {
    onCompleted: (data) => {
      if (debug) {
          console.log ('data =');
          console.log (data);
      }
      setChangeEmailMoadl(false);
      setChangeConfirmationMoadal(true);
    },
    onError: (error) => {
      const isEmailAlreadyVerified = error.graphQLErrors.some(
        (graphQLError) =>
          graphQLError.extensions?.code === 'EMAIL_ALREADY_VERIFIED'
      );

      if (isEmailAlreadyVerified) {
        setChangeEmailMoadl(false);
        setChangeConfirmationMoadal(false);
        setNewEmailError(false);
        setNewEmailErrorText('');

        dashboardChangeEmail({
          variables: {
            input: {
              email: newEmailValue,
            },
          },
        });

        return;
      }

      const content = error.message;

      setNewEmailErrorText(content);
      setNewEmailError(true);

      if (debug) {
        console.log(error);
      }
    },
  });

  const handleVerificationCodeChange = (newValues: string[]) => {
    setNewVerificationCodeError (false);
    setVerificationCode(newValues);
  };

  const handleSendNewEmail = () => {
    let noErrors = true;

    if (!isValidEmail(newEmailValue)) {
      setNewEmailErrorText("Please, enter correct e-mail.");
      setNewEmailError(true);
      noErrors = false;
    }

    if (noErrors) {
      setNewEmailErrorText('');
      setNewEmailError(false);

      setCodeResent(false);
      setVerificationCode([]);
      setNewVerificationCodeError(false);
      dashboardSendEmailVerification({
        variables: {
          input: {
            oldEmail: emailValue,
            newEmail: newEmailValue,
          },
        },
      });
    }
  };

  const handleReSendNewEmail = () => {

    setVerificationCode([]);
    setNewVerificationCodeError (false);
    setCodeResent (true);
      dashboardSendEmailVerification({
        variables: {
          input: {
            oldEmail: emailValue,
            newEmail: newEmailValue,
          },
        },
      });

  };

  const handleChangeEmail = () => {
    let noErrors = true;

    if (debug) {
      console.log('Verification code:' + verificationCode);
    }
    // converting verificationCode from string[] to string
    let verificationCodeString = verificationCode.join("");

    if (verificationCodeString.length < 6) {
      setNewVerificationCodeErrorText("Please, enter verification code.");
      setNewVerificationCodeError(true);
      noErrors = false;
    }

    if (noErrors) {
      setNewEmailErrorText('');
      setNewEmailError(false);

      dashboardChangeEmail({
        variables: {
          input: {
            email: newEmailValue,
            token: verificationCodeString,
          },
        },
      });
    }
  };

  const handleNewEmailChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setNewEmailError (false);
    setNewEmailValue(e.target.value)
  };

  const handleOpenChangeEmail = () => {
    setNewEmailError (false);
    setNewEmailValue(emailValue);
    setChangeEmailMoadl(true);
    if (debug) {
      console.log(changeEmailMoad);
      console.log(changeConfirmationMoadal);
    }
  };

  const handleCloseChangeEmail = () => {
    setChangeEmailMoadl(false);
  };

  const handleCloseConfirmation = () => {
    setChangeConfirmationMoadal(false);
  };

  return {
    emailValue,
    newEmailValue,
    changeEmailMoad,
    changeConfirmationMoadal,
    newEmailError,
    newEmailErrorText,
    newVerificationCodeError,
    newVerificationCodeErrorText,
    codeResent,
    CODE_LENGTH,
    verificationCode,
    handleNewEmailChange,
    handleOpenChangeEmail,
    handleCloseChangeEmail,
    handleCloseConfirmation,
    handleVerificationCodeChange,
    handleSendNewEmail,
    handleReSendNewEmail,
    handleChangeEmail
  };
};
